import { useCallback, useEffect, useState } from 'react'
import {
  attendanceCreateShift,
  attendanceDeleteShift,
  attendanceLoadShifts,
  attendanceUpdateShift,
  type AttendanceShift,
} from '../../lib/apiPhpAdapter'
import './AttendanceOrgAdmin.css'

type Props = {
  showAlert: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  embedded?: boolean
  onChanged?: () => void | Promise<void>
}

type FormState = {
  name: string
  wage_code: string
  premium_wage_code: string
  time_from: string
  time_to: string
  break_from: string
  break_to: string
  crosses_midnight: boolean
  fixed_boundaries: boolean
  active: boolean
}

function timeForInput(raw: string | null | undefined, fallback = ''): string {
  const s = String(raw || '').trim()
  const m = s.match(/^(\d{2}):(\d{2})/)
  return m ? `${m[1]}:${m[2]}` : fallback
}

function breakMinutesBetween(from: string, to: string): number {
  if (!from || !to) return 0
  const [fh, fm] = from.split(':').map((x) => parseInt(x, 10))
  const [th, tm] = to.split(':').map((x) => parseInt(x, 10))
  if (![fh, fm, th, tm].every((n) => Number.isFinite(n))) return 0
  let mins = th * 60 + tm - (fh * 60 + fm)
  if (mins < 0) mins += 24 * 60
  return mins
}

const emptyForm = (): FormState => ({
  name: '',
  wage_code: '',
  premium_wage_code: '',
  time_from: '07:00',
  time_to: '15:30',
  break_from: '09:30',
  break_to: '10:00',
  crosses_midnight: false,
  fixed_boundaries: false,
  active: true,
})

function formFromShift(s: AttendanceShift): FormState {
  const hasBreakRange = Boolean(s.break_from && s.break_to)
  const mins = Number(s.break_minutes) || 0
  let breakFrom = timeForInput(s.break_from, '')
  let breakTo = timeForInput(s.break_to, '')
  // Legacy: jen minuty → odhad intervalu přestávky
  if (!hasBreakRange && mins > 0) {
    breakFrom = '09:30'
    const end = 9 * 60 + 30 + mins
    breakTo = `${String(Math.floor(end / 60) % 24).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}`
  }
  return {
    name: s.name || '',
    wage_code: s.wage_code || '',
    premium_wage_code: s.premium_wage_code || '',
    time_from: timeForInput(s.time_from, '07:00'),
    time_to: timeForInput(s.time_to, '15:30'),
    break_from: breakFrom,
    break_to: breakTo,
    crosses_midnight: Boolean(Number(s.crosses_midnight)),
    fixed_boundaries: Boolean(s.fixed_boundaries),
    active: s.active === undefined || s.active === null ? true : Boolean(Number(s.active)),
  }
}

export function AttendanceShiftsPanel({ showAlert, embedded = false, onChanged }: Props) {
  const [items, setItems] = useState<AttendanceShift[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [createForm, setCreateForm] = useState<FormState>(emptyForm)
  const [editItem, setEditItem] = useState<AttendanceShift | null>(null)
  const [editForm, setEditForm] = useState<FormState>(emptyForm)
  const [deleteItem, setDeleteItem] = useState<AttendanceShift | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const list = await attendanceLoadShifts()
      setItems(Array.isArray(list) ? list : [])
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Nepodařilo se načíst směny', 'error')
    } finally {
      setLoading(false)
    }
  }, [showAlert])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(() => {
    if (!createOpen && !editItem && !deleteItem) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) {
        setCreateOpen(false)
        setEditItem(null)
        setDeleteItem(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [createOpen, editItem, deleteItem, busy])

  const openCreate = () => {
    setEditItem(null)
    setDeleteItem(null)
    setCreateForm(emptyForm())
    setCreateOpen(true)
  }

  const openEdit = (it: AttendanceShift) => {
    setCreateOpen(false)
    setDeleteItem(null)
    setEditItem(it)
    setEditForm(formFromShift(it))
  }

  const openDelete = (it: AttendanceShift) => {
    setCreateOpen(false)
    setEditItem(null)
    setDeleteItem(it)
  }

  const onCreate = async () => {
    if (!createForm.name.trim() || !createForm.time_from || !createForm.time_to) {
      showAlert('Název a čas směny jsou povinné', 'warning')
      return
    }
    const hasBreak = Boolean(createForm.break_from && createForm.break_to)
    if ((createForm.break_from || createForm.break_to) && !hasBreak) {
      showAlert('Přestávka: vyplňte Od i Do', 'warning')
      return
    }
    setBusy(true)
    try {
      const pause = hasBreak ? breakMinutesBetween(createForm.break_from, createForm.break_to) : 0
      await attendanceCreateShift({
        name: createForm.name.trim(),
        wage_code: createForm.wage_code.trim() || undefined,
        premium_wage_code: createForm.premium_wage_code.trim() || undefined,
        time_from: createForm.time_from,
        time_to: createForm.time_to,
        break_from: hasBreak ? createForm.break_from : null,
        break_to: hasBreak ? createForm.break_to : null,
        break_minutes: pause,
        crosses_midnight: createForm.crosses_midnight,
        fixed_boundaries: createForm.fixed_boundaries,
      })
      showAlert('Směna vytvořena', 'success')
      setCreateForm(emptyForm())
      setCreateOpen(false)
      await reload()
      await onChanged?.()
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Uložení selhalo', 'error')
    } finally {
      setBusy(false)
    }
  }

  const onSaveEdit = async () => {
    if (!editItem) return
    if (!editForm.name.trim() || !editForm.time_from || !editForm.time_to) {
      showAlert('Název a čas směny jsou povinné', 'warning')
      return
    }
    const hasBreak = Boolean(editForm.break_from && editForm.break_to)
    if ((editForm.break_from || editForm.break_to) && !hasBreak) {
      showAlert('Přestávka: vyplňte Od i Do', 'warning')
      return
    }
    setBusy(true)
    try {
      const pause = hasBreak ? breakMinutesBetween(editForm.break_from, editForm.break_to) : 0
      await attendanceUpdateShift(editItem.id, {
        name: editForm.name.trim(),
        wage_code: editForm.wage_code.trim() || null,
        premium_wage_code: editForm.premium_wage_code.trim() || null,
        time_from: editForm.time_from,
        time_to: editForm.time_to,
        break_from: hasBreak ? editForm.break_from : null,
        break_to: hasBreak ? editForm.break_to : null,
        break_minutes: pause,
        crosses_midnight: editForm.crosses_midnight,
        fixed_boundaries: editForm.fixed_boundaries,
        active: editForm.active,
      })
      showAlert('Směna upravena', 'success')
      setEditItem(null)
      await reload()
      await onChanged?.()
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Uložení selhalo', 'error')
    } finally {
      setBusy(false)
    }
  }

  const onConfirmDelete = async () => {
    if (!deleteItem) return
    setBusy(true)
    try {
      await attendanceDeleteShift(deleteItem.id)
      showAlert('Smazáno', 'success')
      setDeleteItem(null)
      await reload()
      await onChanged?.()
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Smazání selhalo', 'error')
    } finally {
      setBusy(false)
    }
  }

  const Wrapper = embedded ? 'div' : 'section'
  const wrapperClass = embedded ? 'att-cat' : 'attendance-panel att-cat'

  const renderModalFields = (form: FormState, setForm: (fn: (f: FormState) => FormState) => void, opts?: { showActive?: boolean }) => (
    <div className="att-abs-modal__fields">
      <label>
        Název
        <input
          value={form.name}
          disabled={busy}
          autoFocus={!opts?.showActive}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        />
      </label>
      <label>
        Mzdový kód
        <input
          value={form.wage_code}
          disabled={busy}
          onChange={(e) => setForm((f) => ({ ...f, wage_code: e.target.value }))}
        />
      </label>
      <label>
        Příplatek (kód)
        <input
          value={form.premium_wage_code}
          disabled={busy}
          onChange={(e) => setForm((f) => ({ ...f, premium_wage_code: e.target.value }))}
        />
      </label>
      <div className="att-abs-modal__time-row att-abs-modal__time-row--2">
        <label>
          Směna od
          <input
            type="time"
            value={form.time_from}
            disabled={busy}
            onChange={(e) => setForm((f) => ({ ...f, time_from: e.target.value }))}
          />
        </label>
        <label>
          Směna do
          <input
            type="time"
            value={form.time_to}
            disabled={busy}
            onChange={(e) => setForm((f) => ({ ...f, time_to: e.target.value }))}
          />
        </label>
      </div>
      <div className="att-abs-modal__time-row att-abs-modal__time-row--2">
        <label>
          Přestávka od
          <input
            type="time"
            value={form.break_from}
            disabled={busy}
            onChange={(e) => setForm((f) => ({ ...f, break_from: e.target.value }))}
          />
        </label>
        <label>
          Přestávka do
          <input
            type="time"
            value={form.break_to}
            disabled={busy}
            onChange={(e) => setForm((f) => ({ ...f, break_to: e.target.value }))}
          />
        </label>
      </div>
      <p className="att-abs-modal__hint">
        Přestávka {breakMinutesBetween(form.break_from, form.break_to)} min — zapisuje se automaticky
        každý pracovní den (není ruční událost).
      </p>
      <label className="att-abs-modal__check">
        <input
          type="checkbox"
          checked={form.crosses_midnight}
          disabled={busy}
          onChange={(e) => setForm((f) => ({ ...f, crosses_midnight: e.target.checked }))}
        />
        přes půlnoc
      </label>
      <label className="att-abs-modal__check">
        <input
          type="checkbox"
          checked={form.fixed_boundaries}
          disabled={busy}
          onChange={(e) => setForm((f) => ({ ...f, fixed_boundaries: e.target.checked }))}
        />
        pevné hranice
      </label>
      {opts?.showActive ? (
        <label className="att-abs-modal__check">
          <input
            type="checkbox"
            checked={form.active}
            disabled={busy}
            onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
          />
          aktivní
        </label>
      ) : null}
    </div>
  )

  return (
    <Wrapper className={wrapperClass}>
      {!embedded ? <h2>Karta směn</h2> : null}
      <p className="att-cat__intro">
        Správa směn (čas směny, interval přestávky Od–Do, mzdové kódy).
      </p>

      <div className="att-org__add att-cat__add">
        <button type="button" className="system-button" disabled={busy} onClick={openCreate}>
          Přidat směnu
        </button>
      </div>

      {loading ? <p className={embedded ? 'att-prehled__muted' : 'attendance-muted'}>Načítání…</p> : null}

      <div className="att-cat__wrap">
        <table className="att-cat__table att-cat__table--shifts">
          <thead>
            <tr>
              <th className="att-cat__col-name">Název</th>
              <th className="att-cat__col-wage">Mzd. kód</th>
              <th className="att-cat__col-wage">Přípl.</th>
              <th className="att-cat__col-time">Čas směny</th>
              <th className="att-cat__col-time">Přestávka</th>
              <th className="att-cat__col-narrow">Hranice</th>
              <th className="att-cat__col-narrow">Stav</th>
              <th className="att-cat__col-actions att-cat__col-fill" aria-label="Akce" />
            </tr>
          </thead>
          <tbody>
            {items.map((s) => {
              const isActive = s.active === undefined || s.active === null ? true : Boolean(Number(s.active))
              const timeLabel = `${timeForInput(s.time_from, '—')}–${timeForInput(s.time_to, '—')}${s.crosses_midnight ? ' +1' : ''}`
              const breakLabel =
                s.break_from && s.break_to
                  ? `${timeForInput(s.break_from)}–${timeForInput(s.break_to)}`
                  : Number(s.break_minutes) > 0
                    ? `${s.break_minutes}′`
                    : '—'
              return (
                <tr key={s.id} className={isActive ? undefined : 'att-cat__row--inactive'}>
                  <td className="att-cat__col-name" title={s.name}>
                    {s.name}
                  </td>
                  <td className="att-cat__col-wage" title={s.wage_code || undefined}>
                    {s.wage_code || '—'}
                  </td>
                  <td className="att-cat__col-wage" title={s.premium_wage_code || undefined}>
                    {s.premium_wage_code || '—'}
                  </td>
                  <td className="att-cat__col-time" title={timeLabel}>
                    {timeLabel}
                  </td>
                  <td className="att-cat__col-time" title={breakLabel}>
                    {breakLabel}
                  </td>
                  <td className="att-cat__col-narrow">{s.fixed_boundaries ? 'pevné' : '—'}</td>
                  <td className="att-cat__col-narrow">{isActive ? 'aktivní' : 'neaktivní'}</td>
                  <td className="att-cat__col-actions">
                    <button
                      type="button"
                      className="system-button att-cat__btn"
                      disabled={busy}
                      onClick={() => openEdit(s)}
                    >
                      Upravit
                    </button>
                    <button
                      type="button"
                      className="system-button att-cat__btn att-cat__btn--danger"
                      disabled={busy}
                      onClick={() => openDelete(s)}
                    >
                      Smazat
                    </button>
                  </td>
                </tr>
              )
            })}
            {!loading && items.length === 0 ? (
              <tr>
                <td colSpan={8} className="att-cat__empty">
                  Žádné směny.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {createOpen ? (
        <div
          className="att-abs-modal-overlay"
          role="presentation"
          onClick={() => {
            if (!busy) setCreateOpen(false)
          }}
        >
          <div
            className="att-abs-modal"
            style={{ width: 'min(560px, 100%)' }}
            role="dialog"
            aria-modal
            aria-labelledby="att-shift-create-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="att-shift-create-title">Nová směna</h3>
            {renderModalFields(createForm, setCreateForm)}
            <div className="att-abs-modal__actions">
              <button
                type="button"
                className="system-button"
                disabled={busy || !createForm.name.trim()}
                onClick={() => void onCreate()}
              >
                {busy ? 'Ukládám…' : 'Vytvořit'}
              </button>
              <button
                type="button"
                className="system-button"
                style={{ background: '#64748b' }}
                disabled={busy}
                onClick={() => setCreateOpen(false)}
              >
                Zrušit
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editItem ? (
        <div
          className="att-abs-modal-overlay"
          role="presentation"
          onClick={() => {
            if (!busy) setEditItem(null)
          }}
        >
          <div
            className="att-abs-modal"
            style={{ width: 'min(560px, 100%)' }}
            role="dialog"
            aria-modal
            aria-labelledby="att-shift-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="att-shift-edit-title">Upravit směnu</h3>
            {renderModalFields(editForm, setEditForm, { showActive: true })}
            <div className="att-abs-modal__actions">
              <button
                type="button"
                className="system-button"
                disabled={busy || !editForm.name.trim()}
                onClick={() => void onSaveEdit()}
              >
                {busy ? 'Ukládám…' : 'Uložit'}
              </button>
              <button
                type="button"
                className="system-button"
                style={{ background: '#64748b' }}
                disabled={busy}
                onClick={() => setEditItem(null)}
              >
                Zrušit
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteItem ? (
        <div
          className="att-abs-modal-overlay"
          role="presentation"
          onClick={() => {
            if (!busy) setDeleteItem(null)
          }}
        >
          <div
            className="att-abs-modal"
            role="dialog"
            aria-modal
            aria-labelledby="att-shift-del-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="att-shift-del-title">Smazat směnu?</h3>
            <p>
              Opravdu smazat <strong>{deleteItem.name}</strong>?
            </p>
            <div className="att-abs-modal__actions">
              <button
                type="button"
                className="system-button att-cat__btn--danger"
                disabled={busy}
                onClick={() => void onConfirmDelete()}
              >
                {busy ? 'Mazání…' : 'Smazat'}
              </button>
              <button
                type="button"
                className="system-button"
                style={{ background: '#64748b' }}
                disabled={busy}
                onClick={() => setDeleteItem(null)}
              >
                Zrušit
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </Wrapper>
  )
}
